import {
  ChangeEvent,
  KeyboardEvent,
  UIEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Typography,
} from '@mui/material';

import { IcEdit } from '@/assets/images';
import { Button } from '@/components/TailwindComponents/base/Button/Button';
import { IUser } from '@/models';
import { ConfirmTypes, useToastStore } from '@/store';
import { colors } from '@/theme/variables';
import { formatPersonName } from '@/utils/masterName';
import {
  getMentionAnchorPosition,
  MentionAnchorPosition,
} from '@/utils/mentionAnchor';

import { Input } from '../../FormElements/Input';
import { VoiceToText } from '../../VoiceToText';

type MentionOption = {
  id: string;
  username: string;
  displayName: string;
  // 'First Last' as stored by pre-migration texts; kept so editing an old
  // note still highlights its mentions. Insertion always uses displayName.
  legacyName: string;
};

type MentionPayload = {
  userId: string;
  username: string;
  displayName: string;
};

interface AdditionalInfoInputProps {
  label?: string;
  placeholder?: string;
  onSubmit?: (value: string, isEditing: boolean) => void;
  onSubmitWithMentions?: (
    value: string,
    mentions: MentionPayload[],
    isEditing: boolean,
  ) => void;
  initialValue?: string;
  isEditing?: boolean;
  resetEdit?: () => void;
  maxChars?: number;
  showCharCounter?: boolean;
  mentionUsers?: IUser[];
  mirrorOffsetX?: number;
}

export const AdditionalInfoInput = (props: AdditionalInfoInputProps) => {
  const {
    onSubmit,
    onSubmitWithMentions,
    initialValue = '',
    isEditing = false,
    resetEdit,
    maxChars,
    showCharCounter = !!maxChars,
    mentionUsers = [],
    mirrorOffsetX = 0,
    ...rest
  } = props;
  const [value, setValue] = useState(initialValue);
  const [charCount, setCharCount] = useState(initialValue?.length || 0);
  const isOverLimit = maxChars ? charCount > maxChars : false;
  const { updateToast } = useToastStore();
  const inputRef = useRef<any>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);

  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionAnchor, setMentionAnchor] =
    useState<MentionAnchorPosition | null>(null);
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const [selectedMentions, setSelectedMentions] = useState<
    Map<string, MentionPayload>
  >(new Map());
  const editAdornmentWidth = 28;

  const mentionOptions = useMemo<MentionOption[]>(() => {
    return (mentionUsers || [])
      .map((user) => {
        const username = user.username || user.email?.split('@')[0] || '';
        const displayName = formatPersonName(user);
        return {
          id: user._id || '',
          username,
          displayName: displayName || user.fullName || username,
          legacyName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        };
      })
      .filter((option) => option.username);
  }, [mentionUsers]);

  const mentionTokens = useMemo(() => {
    const tokenSet = new Set<string>();
    mentionOptions.forEach((option) => {
      if (option.displayName) {
        tokenSet.add(`@${option.displayName}`);
      }
      if (option.legacyName && option.legacyName !== option.displayName) {
        tokenSet.add(`@${option.legacyName}`);
      }
      if (option.username) {
        tokenSet.add(`@${option.username}`);
      }
    });
    return Array.from(tokenSet).sort((a, b) => b.length - a.length);
  }, [mentionOptions]);

  const highlightedValue = useMemo(() => {
    if (!value) return '';
    if (!mentionTokens.length) return value;
    const parts: Array<string | JSX.Element> = [];
    let index = 0;
    while (index < value.length) {
      let matchToken: string | undefined;
      for (const token of mentionTokens) {
        if (value.startsWith(token, index)) {
          matchToken = token;
          break;
        }
      }
      if (matchToken) {
        const mentionText = value.slice(index, index + matchToken.length);
        parts.push(
          <span
            key={`mention-${index}`}
            style={{
              display: 'inline',
              backgroundColor: 'rgba(255, 239, 179, 0.7)',
              color: colors.warning.main,
              fontWeight: 400,
              borderRadius: '4px',
              boxShadow:
                '2px 0 0 rgba(255, 239, 179, 0.7), -2px 0 0 rgba(255, 239, 179, 0.7)',
              boxDecorationBreak: 'clone',
            }}
          >
            {mentionText}
          </span>,
        );
        index += matchToken.length;
      } else {
        parts.push(value[index]);
        index += 1;
      }
    }
    return parts;
  }, [mentionTokens, value]);

  const normalizeSpaces = (input: string) => {
    return input.replace(/\s+/g, ' ').trim();
  };

  const filteredMentions = useMemo(() => {
    if (!mentionOpen) return [];
    const query = normalizeSpaces(mentionQuery).toLowerCase();
    if (!query) return mentionOptions;
    return mentionOptions.filter((option) => {
      const nameMatch = normalizeSpaces(option.displayName)
        .toLowerCase()
        .includes(query);
      // Old muscle memory: users typed '@First La…' for years — keep the
      // legacy order matchable even though insertion uses displayName.
      const legacyMatch = normalizeSpaces(option.legacyName)
        .toLowerCase()
        .includes(query);
      const usernameMatch = option.username.toLowerCase().includes(query);
      return nameMatch || legacyMatch || usernameMatch;
    });
  }, [mentionOpen, mentionQuery, mentionOptions]);

  useEffect(() => {
    setValue(initialValue || '');
    setCharCount(initialValue?.length || 0);
    setSelectedMentions(new Map());
  }, [initialValue]);

  useEffect(() => {
    if (mirrorRef.current && inputRef.current) {
      mirrorRef.current.scrollTop = inputRef.current.scrollTop || 0;
    }
  }, [value]);

  const buildMentionsFromText = (text: string): MentionPayload[] => {
    if (!text || selectedMentions.size === 0) return [];
    const normalizedText = normalizeSpaces(text);
    return Array.from(selectedMentions.values()).filter((mention) => {
      const displayToken = normalizeSpaces(`@${mention.displayName}`);
      const usernameToken = normalizeSpaces(`@${mention.username}`);
      return (
        normalizedText.includes(displayToken) ||
        normalizedText.includes(usernameToken)
      );
    });
  };

  const updateMentionContext = (
    text: string,
    cursorPos: number | null,
    inputElement?: HTMLTextAreaElement | null,
  ) => {
    const textarea =
      inputElement || (inputRef.current as HTMLTextAreaElement | null);

    if (!mentionOptions.length) {
      setMentionOpen(false);
      setMentionQuery('');
      setMentionStart(null);
      setMentionAnchor(null);
      return;
    }
    if (cursorPos === null) {
      setMentionOpen(false);
      setMentionQuery('');
      setMentionStart(null);
      setMentionAnchor(null);
      return;
    }
    const lastAt = text.lastIndexOf('@', cursorPos - 1);
    if (lastAt === -1) {
      setMentionOpen(false);
      setMentionQuery('');
      setMentionStart(null);
      setMentionAnchor(null);
      return;
    }
    const charBefore = lastAt > 0 ? text[lastAt - 1] : '';
    if (charBefore && !/\s/.test(charBefore)) {
      setMentionOpen(false);
      setMentionQuery('');
      setMentionStart(null);
      setMentionAnchor(null);
      return;
    }
    const between = text.slice(lastAt + 1, cursorPos);
    if (/[\r\n]/.test(between)) {
      setMentionOpen(false);
      setMentionQuery('');
      setMentionStart(null);
      setMentionAnchor(null);
      return;
    }
    setMentionOpen(true);
    setMentionQuery(between);
    setMentionStart(lastAt);
    if (textarea) {
      setMentionAnchor(getMentionAnchorPosition(textarea, lastAt));
    }
    setActiveMentionIndex(0);
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (mentionOpen && filteredMentions.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveMentionIndex((prev) =>
          prev + 1 >= filteredMentions.length ? 0 : prev + 1,
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveMentionIndex((prev) =>
          prev - 1 < 0 ? filteredMentions.length - 1 : prev - 1,
        );
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const option = filteredMentions[activeMentionIndex];
        if (option) {
          handleMentionSelect(option);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionOpen(false);
        setMentionQuery('');
        setMentionStart(null);
        setMentionAnchor(null);
        return;
      }
    }
    if (e.key === 'Enter' && !isOverLimit) {
      e.preventDefault();
      handleSubmit();
    }

    if (e.key === 'Escape' && isEditing && resetEdit) {
      resetEdit();
      setValue('');
      setCharCount(0);
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    setCharCount(newValue.length);
    updateMentionContext(
      newValue,
      e.target.selectionStart,
      e.target as HTMLTextAreaElement,
    );
  };

  const handleSubmit = () => {
    if (value && !isOverLimit) {
      if (onSubmitWithMentions) {
        const mentions = buildMentionsFromText(value);
        onSubmitWithMentions(value, mentions, isEditing);
      } else if (onSubmit) {
        onSubmit(value, isEditing);
      }
      if (!isEditing) {
        setValue('');
        setCharCount(0);
        setMentionOpen(false);
        setMentionQuery('');
        setMentionStart(null);
        setMentionAnchor(null);
      }
    }
  };

  const handleVoiceToTextOutput = (transcript: string) => {
    const newValue = value + (value ? ' ' : '') + transcript;
    setValue(newValue);
    setCharCount(newValue.length);
  };

  const handleVoiceToTextError = (errorMsg: string) => {
    updateToast({ open: true, message: errorMsg, type: ConfirmTypes.WARNING });
  };

  const voiceToTextIcon = () => {
    return (
      <VoiceToText
        className="flex items-center"
        handleTextOutput={handleVoiceToTextOutput}
        handleError={handleVoiceToTextError}
        variant="stop-button"
      />
    );
  };

  const sendEditIcon = () => {
    if (!isEditing) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: `${editAdornmentWidth}px`,
          justifyContent: 'center',
          marginRight: '2px',
        }}
      >
        <IconButton
          type="button"
          onClick={handleSubmit}
          disabled={!value || isOverLimit}
          sx={{
            padding: 0,
            opacity: !value || isOverLimit ? 0.5 : 1,
            cursor: !value || isOverLimit ? 'not-allowed' : 'pointer',
          }}
        >
          <IcEdit color={!value || isOverLimit ? '#aaa' : '#EE3E5C'} />
        </IconButton>
      </Box>
    );
  };

  useEffect(() => {
    if (!value && !initialValue && isEditing && resetEdit) {
      resetEdit();
    }
  }, [value, initialValue, isEditing, resetEdit]);

  const inputProps = { ...(maxChars ? { maxLength: maxChars + 100 } : {}) };
  const handleInputScroll = (event: UIEvent<HTMLTextAreaElement>) => {
    if (mirrorRef.current) {
      mirrorRef.current.scrollTop = event.currentTarget.scrollTop;
    }
    if (mentionOpen && mentionStart !== null) {
      setMentionAnchor(
        getMentionAnchorPosition(event.currentTarget, mentionStart),
      );
    }
  };

  const handleMentionSelect = (option: MentionOption) => {
    if (!inputRef.current) return;
    const cursorPos = inputRef.current.selectionStart ?? value.length;
    let resolvedStart = mentionStart;
    if (resolvedStart === null) {
      const lastAt = value.lastIndexOf('@', cursorPos - 1);
      resolvedStart = lastAt >= 0 ? lastAt : null;
    }
    if (resolvedStart === null) return;
    let replaceEnd = resolvedStart + 1;
    const mentionText = value.slice(replaceEnd);
    const mentionMatch = mentionText.match(
      /^[a-zA-Z0-9_.]+(?:\s+[a-zA-Z0-9_.]+)*/,
    );
    if (mentionMatch) {
      replaceEnd = resolvedStart + 1 + mentionMatch[0].length;
    }
    const before = value.slice(0, resolvedStart);
    const after = value.slice(replaceEnd);
    const inserted = `@${option.displayName}`;
    const nextValue = `${before}${inserted} ${after}`;
    const insertEnd = resolvedStart + inserted.length;

    setValue(nextValue);
    setCharCount(nextValue.length);
    setSelectedMentions((prev) => {
      const next = new Map(prev);
      next.set(option.username, {
        userId: option.id,
        username: option.username,
        displayName: option.displayName,
      });
      return next;
    });
    setMentionOpen(false);
    setMentionQuery('');
    setMentionStart(null);
    setMentionAnchor(null);
    const nextPos = Math.min(insertEnd + 1, nextValue.length);
    inputRef.current.focus();
    inputRef.current.setSelectionRange(nextPos, nextPos);
  };

  const mentionAnchorEl = mentionAnchor
    ? {
        getBoundingClientRect: () =>
          new DOMRect(mentionAnchor.left, mentionAnchor.top, 0, 0),
        contextElement: containerRef.current || undefined,
      }
    : containerRef.current;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box ref={containerRef} sx={{ position: 'relative', flex: 1 }}>
          {value && (
            <Box
              ref={mirrorRef}
              aria-hidden
              sx={{
                position: 'absolute',
                inset: 0,
                padding: '7px 10px',
                paddingLeft: isEditing
                  ? `${10 + editAdornmentWidth + mirrorOffsetX}px`
                  : `${10 + mirrorOffsetX}px`,
                paddingRight: '30px',
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                color: 'text.primary',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                pointerEvents: 'none',
                overflow: 'hidden',
              }}
            >
              {highlightedValue}
            </Box>
          )}
          <Input
            name="additionalInfo"
            value={value}
            onKeyDown={handleKeyDown}
            onChange={onChange}
            onClick={(e) =>
              updateMentionContext(
                value,
                (e.target as HTMLTextAreaElement).selectionStart,
                e.target as HTMLTextAreaElement,
              )
            }
            {...rest}
            startAdornment={sendEditIcon()}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                position: 'relative',
                zIndex: 1,
              },
              '& .MuiOutlinedInput-root': {
                alignItems: 'flex-start',
                paddingTop: '3px',
                paddingBottom: '3px',
              },
              '& .MuiInputBase-input': {
                paddingTop: '3px',
                paddingBottom: '3px',
                ...(value && {
                  color: 'transparent',
                  WebkitTextFillColor: 'transparent',
                  caretColor: colors.grey[80],
                }),
              },
              '& .MuiInputBase-multiline': {},
              '& textarea': {
                paddingRight: '30px',
              },
              '& textarea::placeholder': {
                color: 'text.secondary',
              },
              ...(isOverLimit && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'error.main',
                  },
                  '&:hover fieldset': {
                    borderColor: 'error.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'error.main',
                  },
                },
              }),
            }}
            multiline
            fullWidth
            ref={inputRef}
            inputProps={{
              ...inputProps,
              onScroll: handleInputScroll,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '-2px',
              right: '-2px',
              zIndex: 1,
              transform: 'scale(0.7)',
            }}
          >
            {voiceToTextIcon()}
          </Box>
        </Box>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!value || isOverLimit}
          variant="destructive"
          size="icon"
          className={`h-[34px] w-[32px] min-w-[32px] ${
            !value || isOverLimit
              ? 'opacity-50 cursor-not-allowed'
              : 'opacity-100 cursor-pointer'
          }`}
        >
          <SendIcon className="text-white scale-150" />
        </Button>
      </Box>
      <Popper
        open={mentionOpen && filteredMentions.length > 0}
        anchorEl={mentionAnchorEl as any}
        placement="top-start"
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 12],
            },
          },
        ]}
        style={{ zIndex: 1300 }}
        disablePortal
      >
        <Paper
          ref={popperRef}
          onMouseDown={(event) => event.preventDefault()}
          sx={{
            width: '300px',
            maxHeight: '240px',
            overflowY: 'auto',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <List dense>
            {filteredMentions.map((option, index) => (
              <ListItemButton
                key={option.id || option.username}
                selected={index === activeMentionIndex}
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleMentionSelect(option);
                }}
              >
                <ListItemText
                  primary={
                    <Typography fontSize="14px" fontWeight={500}>
                      {option.displayName}
                    </Typography>
                  }
                  secondary={null}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </Popper>
      {showCharCounter && maxChars && (
        <Box
          display="flex"
          justifyContent="flex-end"
          sx={{
            color: isOverLimit ? 'error.main' : 'text.secondary',
            fontSize: '0.75rem',
            marginTop: '2px',
          }}
        >
          <Typography variant="caption">
            {charCount}/{maxChars}
          </Typography>
        </Box>
      )}
    </>
  );
};
